import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import "./clientEvent.css";
import { useAuth } from "../../context/AuthContext";
import useTokenFromUrl from "../../hooks/useTokenFromUrl";
import { bookEvent, validateBooking, createEventPaymentOrder, verifyEventPayment, fetchEventById } from "./clientEventService";
import axios from "axios";

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const BookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const { user } = useAuth();
  const { loading: tokenLoading } = useTokenFromUrl();

  const [eventData, setEventData] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [countryCodes, setCountryCodes] = useState([]);
  const [mobileError, setMobileError] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: "info", message: "" });

  // Load country codes
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
      .then(res => setCountryCodes(res.data?.data || []))
      .catch(() => {});
  }, []);

  // Load event: from location.state if available, else fetch by ID (mobile deep link)
  useEffect(() => {
    if (tokenLoading) return;

    const stateEvent = location.state?.event;
    if (stateEvent) {
      setEventData({
        id: stateEvent.id,
        title: stateEvent.title || "Event",
        description: stateEvent.description || "",
        eventStartTime: stateEvent.eventStartTime,
        eventDuration: stateEvent.eventDuration || 60,
        speakerName: stateEvent.speakerName || "TBD",
        originalPrice: stateEvent.originalPrice || 0,
        price: stateEvent.price || 0,
        cancelled: stateEvent.cancelled || false,
      });
      setEventLoading(false);
    } else if (eventId) {
      fetchEventById(eventId).then(res => {
        if (res.status === 200 && res.data) {
          const e = res.data;
          setEventData({
            id: e.id,
            title: e.title || "Event",
            description: e.description || "",
            eventStartTime: e.eventStartTime,
            eventDuration: e.eventDuration || 60,
            speakerName: e.speakerName || "TBD",
            originalPrice: e.originalPrice || 0,
            price: e.price || 0,
            cancelled: e.cancelled || false,
          });
        }
        setEventLoading(false);
      });
    } else {
      setEventLoading(false);
    }
  }, [tokenLoading, eventId, location.state]);

  // Pre-fill user info from auth context
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setMobile(user.mobile || "");
    }
  }, [user]);

  if (tokenLoading || eventLoading) {
    return (
      <>
        <Navbar />
        <div className="client-container">
          <div className="center">
            <div className="spinner"></div>
            <p>{tokenLoading ? "Signing you in..." : "Loading event..."}</p>
          </div>
        </div>
      </>
    );
  }

  if (!eventData) {
    return (
      <>
        <Navbar />
        <div className="client-container">
          <div className="booking-header">
            <h3>Invalid booking request</h3>
            <p>Event information is missing. Please select an event to book.</p>
            <button className="enhanced-back-btn" onClick={() => navigate("/events")}>← Back to Events</button>
          </div>
        </div>
      </>
    );
  }

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert(prev => ({ ...prev, show: false })), 4000);
  };

  const formatDate = (value) => {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return value; }
  };

  const formatTime = (value) => {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    } catch { return value; }
  };

  const validateForm = () => {
    if (!name.trim()) { showAlert("warning", "Please enter your name"); return false; }
    if (!email.trim()) { showAlert("warning", "Please enter your email"); return false; }
    if (!mobile.trim()) { showAlert("warning", "Please enter your mobile number"); return false; }
    const fullNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    if (!/^\+[1-9]\d{1,14}$/.test(fullNumber)) { showAlert("warning", "Please enter a valid mobile number"); return false; }
    if (!consent) { showAlert("warning", "Please provide consent"); return false; }
    return true;
  };

  const validateBookingData = async () => {
    const fullNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    const validationResponse = await validateBooking(eventData.id, email.trim(), fullNumber.trim());
    if (validationResponse.status !== 200) { showAlert("error", validationResponse.message || "Validation failed"); return false; }
    const v = validationResponse.data?.data || validationResponse.data;
    if (v.emailExists) { showAlert("warning", "This email is already registered for this event"); return false; }
    if (v.mobileExists) { showAlert("warning", "This mobile number is already registered for this event"); return false; }
    if (!v.eventAvailable) { showAlert("error", "Event is no longer available or fully booked"); return false; }
    return true;
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handleBooking = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const isValid = await validateBookingData();
      if (!isValid) return;
      if (eventData.price <= 0) {
        await bookFreeEvent();
      } else {
        await initPaidCheckout();
      }
    } catch (error) {
      showAlert("error", error?.response?.data?.message || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  const bookFreeEvent = async () => {
    const fullMobileNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    const payload = {
      name: name.trim(), email: email.trim(), mobile: fullMobileNumber.trim(),
      countryCode: dialCode, timeZone: userTimeZone, consentGiven: consent, whatsappEnabled: true
    };
    const response = await bookEvent(eventData.id, payload);
    if (response.status === 200) {
      setBookingData(response.data);
      setSuccess(true);
    } else {
      showAlert("error", response.message || "Booking failed");
    }
  };

  const initPaidCheckout = async () => {
    const sdkLoaded = await loadRazorpayScript();
    if (!sdkLoaded) { showAlert("error", "Payment service failed to load. Try again."); return; }
    const fullMobileNumber = mobile.startsWith('+') ? mobile : dialCode + mobile;
    const orderPayload = {
      eventId: eventData.id, name: name.trim(), email: email.trim(),
      mobile: fullMobileNumber.trim(), countryCode: dialCode, timeZone: userTimeZone
    };
    const orderResponse = await createEventPaymentOrder(orderPayload);
    if (orderResponse.status !== 200 && orderResponse.status !== 201) {
      showAlert("error", orderResponse.message || "Unable to start payment");
      return;
    }
    const { orderId, amount, currency } = orderResponse.data;
    openRazorpay(orderId, amount, currency, fullMobileNumber);
  };

  const openRazorpay = (orderId, amount, currency, fullMobileNumber) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount, currency,
      name: "Speak Bold with KK",
      description: eventData.title,
      order_id: orderId,
      prefill: { name: name.trim(), email: email.trim(), contact: fullMobileNumber },
      notes: { eventId: eventData.id, email: email.trim() },
      handler: (response) => { verifyAndBook(response, fullMobileNumber); },
      modal: { ondismiss: () => { showAlert("info", "Payment cancelled"); } },
      theme: { color: "#3b82f6" }
    };
    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const verifyAndBook = async (paymentResponse, fullMobileNumber) => {
    try {
      setLoading(true);
      const verifyPayload = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        eventId: eventData.id, name: name.trim(), email: email.trim(),
        mobile: fullMobileNumber.trim(), countryCode: dialCode,
        timeZone: userTimeZone, consentGiven: consent, whatsappEnabled: true
      };
      const response = await verifyEventPayment(verifyPayload);
      if (response.status === 200) {
        setBookingData(response.data);
        setSuccess(true);
      } else {
        showAlert("error", response.message || "Payment verification failed");
      }
    } catch (error) {
      showAlert("error", "Payment verification failed. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  const isLoggedIn = !!user;
  const isFree = eventData.price <= 0;
  const hasDiscount = eventData.originalPrice > 0 && eventData.originalPrice > eventData.price;

  // SUCCESS UI
  if (success) {
    return (
      <>
        <Navbar />
        <div className="client-container">
          <div className="success-page">
            <div className="success-card">
              <div className="success-icon">🎉</div>
              <h2 className="success-title">Booking Confirmed!</h2>
              <p className="success-message">Your seat has been booked for <strong>{eventData.title}</strong>.</p>
              <div className="success-details">
                <div className="success-detail-item"><span className="success-icon-small">📅</span><span>{formatDate(eventData.eventStartTime)}</span></div>
                <div className="success-detail-item"><span className="success-icon-small">⏰</span><span>{formatTime(eventData.eventStartTime)}</span></div>
                <div className="success-detail-item success-detail-item--small"><span className="success-icon-small">📧</span><span>Confirmation sent to {email}</span></div>
              </div>
              <div className="success-actions">
                <button className="enhanced-book-btn success-btn" onClick={() => navigate("/events")}>Browse More Events</button>
                <button className="secondary-btn" onClick={() => navigate("/my-bookings")}>View My Bookings</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // MAIN UI
  return (
    <>
      <Navbar />

      {alert.show && (
        <div className="alert-overlay">
          <div className={`alert-modal ${alert.type}`}>
            <div className="alert-message">{alert.message}</div>
            <button className="alert-btn" onClick={() => setAlert(prev => ({ ...prev, show: false }))}>OK</button>
          </div>
        </div>
      )}

      <div className="client-container">
        <div className="booking-header-enhanced">
          <button className="enhanced-back-btn" onClick={() => navigate("/events")}>← Back to Events</button>
          <div className="header-content">
            <h1 className="booking-page-title">{eventData.title}</h1>
            <p className="booking-page-subtitle">
              {formatDate(eventData.eventStartTime)} • {formatTime(eventData.eventStartTime)} • {eventData.eventDuration} min
            </p>
          </div>
        </div>

        <div className="booking-form-centered">
          <div className="enhanced-booking-form">
            <div className="form-header">
              <div className="form-icon">👤</div>
              <div>
                <h3 className="form-title">Personal Information</h3>
                <p className="form-subtitle">We'll send your booking confirmation to these details</p>
              </div>
            </div>

            {!isLoggedIn && (
              <div className="form-fields">
                <div className="enhanced-input-group">
                  <label htmlFor="name">Full Name *</label>
                  <div className="input-wrapper">
                    <span className="input-icon">👤</span>
                    <input id="name" type="text" placeholder="Enter your full name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                </div>
                <div className="enhanced-input-group">
                  <label htmlFor="email">Email Address *</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📧</span>
                    <input id="email" type="email" placeholder="Enter your email address" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                </div>
                <div className="enhanced-input-group">
                  <label htmlFor="mobile">Mobile Number *</label>
                  <div className="mobile-row">
                    <select className="country-code-select" value={dialCode} onChange={(e) => setDialCode(e.target.value)}>
                      {countryCodes.length > 0 ? countryCodes.map((c) => (
                        <option key={c.id} value={c.dialCode}>{c.isoCode} {c.dialCode}</option>
                      )) : <option value="+91">IN +91</option>}
                    </select>
                    <div className="input-wrapper mobile-input-wrapper">
                      <span className="input-icon">📱</span>
                      <input id="mobile" type="tel" placeholder="Enter mobile number" autoComplete="tel" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^\d]/g, ""))} maxLength={15} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isLoggedIn && (
              <div className="logged-user-display">
                <div className="user-info-card">
                  <div className="user-detail"><span className="user-icon">👤</span><div><div className="user-label">Full Name</div><div className="user-value">{name}</div></div></div>
                  <div className="user-detail"><span className="user-icon">📧</span><div><div className="user-label">Email Address</div><div className="user-value">{email}</div></div></div>
                  {mobile ? (
                    <div className="user-detail"><span className="user-icon">📱</span><div><div className="user-label">Mobile Number</div><div className="user-value">{mobile}</div></div></div>
                  ) : (
                    <div className="enhanced-input-group mobile-input-logged">
                      <label htmlFor="mobile-logged">Mobile Number *</label>
                      <div className="mobile-row">
                        <select className="country-code-select" value={dialCode} onChange={(e) => setDialCode(e.target.value)}>
                          {countryCodes.length > 0 ? countryCodes.map((c) => (
                            <option key={c.id} value={c.dialCode}>{c.isoCode} {c.dialCode}</option>
                          )) : <option value="+91">IN +91</option>}
                        </select>
                        <div className="input-wrapper mobile-input-wrapper">
                          <span className="input-icon">📱</span>
                          <input id="mobile-logged" type="tel" placeholder="Enter mobile number" autoComplete="tel" value={mobile} onChange={(e) => { setMobile(e.target.value.replace(/[^\d]/g, "")); setMobileError(""); }} maxLength={15} />
                        </div>
                      </div>
                      {mobileError && <span className="field-error">{mobileError}</span>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Consent */}
            <div className="consent-section">
              <label className="enhanced-consent">
                <div className="checkbox-wrapper">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <span className="checkmark"></span>
                </div>
                <div className="consent-text">
                  <strong>I agree to receive event updates and communications</strong>
                  <small>We'll only send relevant information about your bookings.</small>
                </div>
              </label>
            </div>

            {/* Pricing & Action */}
            <div className="booking-actions">
              <div className="payment-summary">
                <div className="payment-amount">
                  <span className="amount-label">Total:</span>
                  <div className="amount-prices">
                    {hasDiscount && <span className="amount-original">₹{eventData.originalPrice}</span>}
                    <span className="amount-value">{isFree ? "FREE" : `₹${eventData.price}`}</span>
                  </div>
                </div>
              </div>

              <button
                className={`enhanced-confirm-btn ${loading || !consent ? 'disabled' : ''}`}
                onClick={handleBooking}
                disabled={loading || !consent}
              >
                {loading ? (
                  <><span className="loading-spinner"></span>Processing...</>
                ) : isFree ? (
                  <><span className="btn-icon">✅</span>Confirm Booking - Free</>
                ) : (
                  <><span className="btn-icon">💳</span>Pay ₹{eventData.price} & Book</>
                )}
              </button>

              <div className="security-badges">
                <div className="security-badge"><span className="security-icon">🔒</span><span>Secure</span></div>
                <div className="security-badge"><span className="security-icon">✅</span><span>Instant Confirmation</span></div>
                {!isFree && <div className="security-badge"><span className="security-icon">💳</span><span>Razorpay</span></div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingPage;
